import {useEffect, useState} from 'react';
import type {
  GetPersonnelListRequest,
  SourceEntityRecord,
} from 'src/api/ProactApiClient';
import {
  EntityTypeEnum,
  UserStatusEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {Paginator} from 'src/design/organisms/Pagination';

import {Box} from 'src/design/atoms/Box';
import {Space} from 'src/design/atoms/Space';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useGetPersonnelListQuery} from 'src/redux/queries/personnel';

export interface ManagerListProps {
  selected: SourceEntityRecord[];
  setSelected: React.Dispatch<React.SetStateAction<SourceEntityRecord[]>>;
  showOnlySelected: boolean;
  showSelectedAll: boolean;
  search?: string;
  itemsPerPage?: number;
  queryOptions?: Partial<GetPersonnelListRequest>;
  /** Callback for unfiltered total count (used for percentage calculation) */
  onTotalCountChange?: (count: number) => void;
  /** Filter managers by project access */
  projectId?: number;
}

export const ManagerList: React.FC<ManagerListProps> = ({
  selected,
  setSelected,
  showOnlySelected,
  showSelectedAll,
  search,
  itemsPerPage = 25,
  queryOptions,
  onTotalCountChange,
  projectId,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const selectedIds = selected.map(i => Number(i.entityId));

  // Baseline query for unfiltered total count (used for percentage calculation)
  const {data: baselineData} = useGetPersonnelListQuery({
    userTypes: [UserTypeEnum.Contractor, UserTypeEnum.Employee],
    hasPendingAction: false,
    superviserOnly: true,
    active: [UserStatusEnum.Active],
    projectIds: projectId ? [projectId] : undefined,
    start: 0,
    length: 0,
    ...queryOptions,
  });

  // Filtered query for display and pagination
  const {
    data: supervisors,
    isFetching: isSupervisorsFetching,
    isLoading: isSupervisorsLoading,
  } = useGetPersonnelListQuery({
    userTypes: [UserTypeEnum.Contractor, UserTypeEnum.Employee],
    hasPendingAction: false,
    superviserOnly: true,
    active: [UserStatusEnum.Active],
    search: search,
    start: currentPage * itemsPerPage - itemsPerPage,
    length: itemsPerPage,
    whitelistIds: showOnlySelected ? selectedIds : undefined,
    projectIds: projectId ? [projectId] : undefined,
    ...queryOptions,
  });

  const isLoading = isSupervisorsFetching || isSupervisorsLoading;

  // Report unfiltered total count for percentage calculation
  useEffect(() => {
    if (onTotalCountChange && baselineData?.totalRecords !== undefined) {
      onTotalCountChange(baselineData.totalRecords);
    }
  }, [baselineData?.totalRecords, onTotalCountChange]);

  function isSelected(id: number): boolean {
    return (
      selected.filter(element => element.entityId.toString() === id.toString())
        .length > 0 || showSelectedAll
    );
  }

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    const {checked} = e.target;
    if (checked) {
      setSelected([
        ...selected,
        {
          entityId: id,
          entityType: EntityTypeEnum.Manager,
        },
      ]);
    } else {
      setSelected(selected.filter(i => i.entityId !== id));
    }
  };

  const handlePageChange = (_: unknown, value: number) => {
    setCurrentPage(value);
  };

  return (
    <Box>
      <Paginator
        mt={8}
        mb={16}
        currentPage={currentPage}
        totalRecords={supervisors?.totalRecords ?? 0}
        perPage={itemsPerPage}
        showRecordCount={false}
        onChange={handlePageChange}
      />
      {!isLoading ? (
        supervisors?.data ? (
          supervisors.data.map(item => (
            <CheckboxRow
              key={item.id}
              id={String(item.id)}
              label={`${item.name}, ${item.jobTitle ?? 'No title'}`}
              checked={isSelected(item.id)}
              onChange={e => handleSelect(e, Number(item.id))}
              disabled={showSelectedAll}
              helpText={`${item?.employeeId} | ${
                item?.department?.description ?? 'No department'
              }`}
            />
          ))
        ) : (
          <Box mt="lg">No selections found</Box>
        )
      ) : (
        <>
          <TableBodyRender isLoading={isLoading} />
          <Space mb="xl" />
        </>
      )}
    </Box>
  );
};
